import { useCallback, useEffect, useRef, useState } from "react";

// A two-pane horizontal split with a draggable divider. The left pane width is
// user-resizable (and persisted to localStorage when a storageKey is given);
// the right pane fills the remaining space.
function ResizableSplit({
  storageKey,
  defaultWidth = 240,
  min = 180,
  max = 520,
  left,
  right,
}) {
  const [width, setWidth] = useState(() => {
    if (storageKey) {
      const saved = parseInt(localStorage.getItem(storageKey) ?? "", 10);
      if (!Number.isNaN(saved)) {
        return Math.min(max, Math.max(min, saved));
      }
    }
    return defaultWidth;
  });
  const draggingRef = useRef(false);

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, String(width));
    }
  }, [width, storageKey]);

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      draggingRef.current = true;
      const startX = e.clientX;
      const startW = width;
      const onMove = (ev) => {
        if (!draggingRef.current) return;
        const next = Math.min(max, Math.max(min, startW + ev.clientX - startX));
        setWidth(next);
      };
      const onUp = () => {
        draggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [width, min, max]
  );

  return (
    <div className="flex h-full w-full overflow-hidden">
      <div
        style={{ width, minWidth: width, maxWidth: width }}
        className="h-full overflow-hidden"
      >
        {left}
      </div>
      <div
        onMouseDown={onMouseDown}
        className="w-1.5 shrink-0 cursor-col-resize bg-base-300 hover:bg-primary/60"
        title="Drag to resize"
      />
      <div className="h-full flex-1 overflow-hidden">{right}</div>
    </div>
  );
}

export default ResizableSplit;
